/**
 * SERP rank-band vocabulary, shared by the SEO-opportunities service (server)
 * and the drill-down UI (client).
 *
 * Bands are ordinal magnitude of ONE measure — your position — which is why
 * `RANK_BAND_ORDER` exists: band movement is the difference between two
 * ordinals (positive = improved), not a difference between ranks. A search
 * that goes 19 → 9 moves one band; 19 → 18 moves none. That coarseness is
 * deliberate: weekly SERP re-checks jitter by a position or two on their own,
 * so only a band change is worth calling movement.
 *
 * Anything outside the top 30 (or absent) is "unranked" — the SERP reads we
 * store never go deeper than 30, so there is no honest distinction between
 * #31 and #500.
 */

export type RankBand = "top3" | "top10" | "top20" | "top30" | "unranked";

/** Ordinal position of each band, best first. Movement = previous − current. */
export const RANK_BAND_ORDER: Record<RankBand, number> = {
	top3: 0,
	top10: 1,
	top20: 2,
	top30: 3,
	unranked: 4,
};

/** Every band, best → worst. Filter dropdowns and chart stacks read this. */
export const RANK_BAND_KEYS: RankBand[] = [
	"top3",
	"top10",
	"top20",
	"top30",
	"unranked",
];

export function bandForRank(rank: number | null | undefined): RankBand {
	if (rank == null || rank < 1 || rank > 30) {
		return "unranked";
	}
	if (rank <= 3) {
		return "top3";
	}
	if (rank <= 10) {
		return "top10";
	}
	if (rank <= 20) {
		return "top20";
	}
	return "top30";
}
